import React, { useRef, useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  ScrollView,
  Dimensions,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import ViewShot from 'react-native-view-shot';
import PageHeader from '@/components/PageHeader';
import ShareableSobrietyCard from '@/components/ShareableSobrietyCard';
import SobrietyCardTemplate2 from '@/components/SobrietyCardTemplate2';
import SobrietyCardTemplate3 from '@/components/SobrietyCardTemplate3';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSobriety } from '@/contexts/SobrietyContext';
import CarouselPagination from '@/components/CarouselPagination';

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_WIDTH = SCREEN_WIDTH - 48;
const CARD_HEIGHT = 390;

let MediaLibrary: typeof import('expo-media-library') | null = null;
let Sharing: typeof import('expo-sharing') | null = null;


export default function ShareSobrietyScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { getCurrentDays } = useSobriety();
  const insets = useSafeAreaInsets();

  const [isSaving, setIsSaving] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const viewShotRef0 = useRef<ViewShot>(null);
  const viewShotRef1 = useRef<ViewShot>(null);
  const viewShotRef2 = useRef<ViewShot>(null);
  const viewShotRefs = [viewShotRef0, viewShotRef1, viewShotRef2];

  const sobrietyDays = getCurrentDays();

  useEffect(() => {
    const loadModules = async () => {
      try {
        MediaLibrary = await import('expo-media-library');
        Sharing = await import('expo-sharing');
        setModulesLoaded(true);
      } catch (error) {
        console.log('Native modules not available in this build:', error);
        setModulesLoaded(false);
      }
    };
    loadModules();
  }, []);

  const handleBack = () => {
    router.back();
  };

  const handleScrollEnd = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(e.nativeEvent.contentOffset.x / SCREEN_WIDTH);
    setActiveIndex(index);
  };

  const captureImage = async (): Promise<string | null> => {
    try {
      const activeRef = viewShotRefs[activeIndex];
      if (activeRef.current?.capture) {
        const uri = await activeRef.current.capture();
        return uri;
      }
      return null;
    } catch (error) {
      console.error('Error capturing image:', error);
      return null;
    }
  };

  const handleSaveToLibrary = async () => {
    if (!MediaLibrary) {
      Alert.alert(
        t('sobriety.sharePage.errorTitle'),
        'This feature requires a new development build. Please rebuild the app.'
      );
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    try {
      const { status } = await MediaLibrary.requestPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          t('sobriety.sharePage.permissionDeniedTitle'),
          t('sobriety.sharePage.permissionDeniedMessage')
        );
        setIsSaving(false);
        return;
      }

      const uri = await captureImage();

      if (uri) {
        await MediaLibrary.saveToLibraryAsync(uri);
        Alert.alert(
          t('sobriety.sharePage.savedTitle'),
          t('sobriety.sharePage.savedMessage')
        );
      } else {
        throw new Error('Failed to capture image');
      }
    } catch (error) {
      console.error('Error saving image:', error);
      Alert.alert(
        t('sobriety.sharePage.errorTitle'),
        t('sobriety.sharePage.errorMessage')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async () => {
    if (!Sharing) {
      Alert.alert(
        t('sobriety.sharePage.errorTitle'),
        'This feature requires a new development build. Please rebuild the app.'
      );
      return;
    }

    if (isSharing) return;
    setIsSharing(true);

    try {
      const uri = await captureImage();

      if (uri) {
        const isAvailable = await Sharing.isAvailableAsync();

        if (isAvailable) {
          await Sharing.shareAsync(uri, {
            mimeType: 'image/png',
            dialogTitle: t('sobriety.sharePage.shareDialogTitle'),
          });
          // PRD §5.5 — `sobriety_share_tapped`. The system share sheet is
          // fire-and-forget — we cannot tell which channel the user
          // picked, so the event has no metadata. Counting taps is
          // sufficient.
          import('@/utils/clarityEvents')
            .then(({ logSobrietyShareTapped }) => logSobrietyShareTapped())
            .catch(() => {});
        } else {
          Alert.alert(
            t('sobriety.sharePage.sharingUnavailableTitle'),
            t('sobriety.sharePage.sharingUnavailableMessage')
          );
        }
      } else {
        throw new Error('Failed to capture image');
      }
    } catch (error) {
      console.error('Error sharing image:', error);
      Alert.alert(
        t('sobriety.sharePage.errorTitle'),
        t('sobriety.sharePage.errorMessage')
      );
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('sobriety.sharePage.title')}
        onBack={handleBack}
        backgroundColor="#F9F8F3"
      />

      <View style={styles.content}>
        <ScrollView
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={handleScrollEnd}
          decelerationRate="fast"
          snapToInterval={SCREEN_WIDTH}
          snapToAlignment="start"
          contentContainerStyle={styles.carouselContent}
          style={styles.carousel}
        >
          <View style={styles.carouselPage}>
            <View style={styles.cardShadowWrapper}>
              <ViewShot
                ref={viewShotRef0}
                options={{ format: 'png', quality: 1 }}
                style={styles.cardWrapper}
              >
                <ShareableSobrietyCard days={sobrietyDays} />
              </ViewShot>
            </View>
          </View>

          <View style={styles.carouselPage}>
            <View style={styles.cardShadowWrapper}>
              <ViewShot
                ref={viewShotRef1}
                options={{ format: 'png', quality: 1 }}
                style={styles.cardWrapper}
              >
                <SobrietyCardTemplate2 days={sobrietyDays} />
              </ViewShot>
            </View>
          </View>

          <View style={styles.carouselPage}>
            <View style={styles.cardShadowWrapper}>
              <ViewShot
                ref={viewShotRef2}
                options={{ format: 'png', quality: 1 }}
                style={styles.cardWrapper}
              >
                <SobrietyCardTemplate3 days={sobrietyDays} />
              </ViewShot>
            </View>
          </View>
        </ScrollView>

        <View style={styles.dotsContainer}>
          <CarouselPagination count={3} activeIndex={activeIndex} />
        </View>
      </View>

      <View style={[styles.actionsContainer, { paddingBottom: 24 + (Platform.OS === 'android' ? insets.bottom : 0) }]}>
        <TouchableOpacity
          style={[styles.actionButton, isSharing && styles.actionButtonDisabled]}
          onPress={handleShare}
          activeOpacity={0.8}
          disabled={isSharing}
        >
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M8.89173 6.73389L6.59375 9.03187" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path fillRule="evenodd" clipRule="evenodd" d="M6.59544 9.03184L2.0521 6.25384C1.45677 5.88984 1.57744 4.99184 2.2481 4.79784L12.9748 1.69917C13.5848 1.5225 14.1488 2.09184 13.9661 2.69984L10.7834 13.3425C10.5841 14.0092 9.6901 14.1238 9.32877 13.5292L6.59544 9.03184Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
          <Text style={styles.actionButtonText}>
            {isSharing ? t('sobriety.sharePage.sharing') : t('sobriety.sharePage.shareButton')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.actionButton, isSaving && styles.actionButtonDisabled]}
          onPress={handleSaveToLibrary}
          activeOpacity={0.8}
          disabled={isSaving}
        >
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path d="M8 10.6667V2" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M10.6667 8L8 10.6667L5.33333 8" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M13.3333 12.6667H2.66667" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
          <Text style={styles.actionButtonText}>
            {isSaving ? t('sobriety.sharePage.saving') : t('sobriety.sharePage.saveButton')}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  carousel: {
    flexGrow: 0,
  },
  carouselContent: {
    alignItems: 'center',
  },
  carouselPage: {
    width: SCREEN_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  cardShadowWrapper: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.1,
    shadowRadius: 48,
    elevation: 24,
    borderRadius: 14,
  },
  cardWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsContainer: {
    marginTop: 16,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    padding: 12,
    height: 70,
    minHeight: 40,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  actionButtonText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    lineHeight: 14,
    color: '#151515',
  },
});
